import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import './FAQPage.css';

const FAQPage = () => {
  const [openQuestions, setOpenQuestions] = useState({});

  const toggleQuestion = (questionId) => {
    setOpenQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const faqData = {
    "General Questions": [
      {
        id: "what-is-wisdom-network",
        question: "What is The Wisdom Network?",
        answer: "The Wisdom Network is an online platform that connects you with certified spiritual life coaches, herbalists, psychic readers, astrologers, healers, manifestation coaches, and other holistic practitioners. Our mission is to provide a trusted space for personal growth, healing, and transformation."
      },
      {
        id: "who-are-practitioners",
        question: "Who are the practitioners?",
        answer: "Our practitioners are highly skilled and certified by Mystic University or other esteemed institutions. They have been rigorously vetted to ensure they possess genuine spiritual gifts and a deep commitment to their craft. You can learn more about each practitioner by browsing their profiles on our website."
      }
    ],
    "Practitioner Verification": [
      {
        id: "how-verified",
        question: "How are practitioners verified?",
        answer: "All practitioners on The Wisdom Network are certified by Mystic University or other respected institutions and undergo rigorous verification for their authenticity and skills. This includes verifying their credentials, assessing their skills, and ensuring they have genuine spiritual gifts. We are committed to providing you with the highest quality practitioners who can deliver on their promises."
      },
      {
        id: "how-ensure-abilities",
        question: "How do you ensure practitioners can do what they claim?",
        answer: "Our vetting process includes reviewing each practitioner's background, qualifications, and client testimonials. Practitioners must demonstrate their abilities through a series of evaluations and tests to prove they can effectively deliver their services. This ensures that only the most capable and trustworthy practitioners join our network."
      },
      {
        id: "best-in-field",
        question: "Are the practitioners really the best in their field?",
        answer: "Yes, we pride ourselves on featuring the best practitioners in their respective fields. Our rigorous selection process ensures that only top-tier practitioners with verified, real spiritual gifts and exceptional skills are included. These are not your average practitioners, as they hold knowledge, wisdom and skills that are not readily available or common for other practitioners. They are many steps above the rest! You can trust that you are receiving guidance from the best of the best."
      }
    ],
    "Booking and Sessions": [
      {
        id: "how-to-book",
        question: "How do I book a session?",
        answer: "You can book a session directly through our booking link. Simply select a practitioner and choose a convenient time for your session. You will receive a confirmation email with all the details, including a Zoom link for your appointment. If you prefer, you can also browse our directory of practitioners to find the one that best fits your needs before booking."
      },
      {
        id: "what-to-expect",
        question: "What should I expect during a session?",
        answer: "During your session, you will connect with your chosen practitioner via Zoom. They will provide personalized guidance, services and support based on their area of expertise. Sessions are designed to help you achieve personal growth, healing, and transformation."
      },
      {
        id: "how-to-prepare",
        question: "How should I prepare for my session?",
        answer: "To get the most out of your session, ensure you are in a quiet and comfortable space where you can focus without distractions. Reflect on your goals, questions, and any specific areas you'd like to focus on to help your practitioner provide the most effective guidance."
      },
      {
        id: "cancellation-policy",
        question: "What is the cancellation policy?",
        answer: "You can cancel or reschedule your session at any time. Use the link provided in your booking confirmation email to make changes to your appointment. However, please note that we do not offer refunds for cancellations."
      }
    ],
    "Privacy and Security": [
      {
        id: "info-secure",
        question: "Is my personal information secure?",
        answer: <>Yes, we take your privacy and security very seriously. We use advanced security measures to protect your personal information. For more details, please refer to our <Link to="/privacy-policy">Privacy Policy</Link>.</>
      },
      {
        id: "session-confidential",
        question: "Will my session be confidential?",
        answer: "Absolutely. All sessions are confidential. Our practitioners are committed to maintaining your privacy and will not share any information discussed during your session without your explicit consent."
      }
    ],
    "Results and Expectations": [
      {
        id: "expect-results",
        question: "How soon can I expect results?",
        answer: "Results vary from person to person and depend on your active participation and commitment. While our practitioners are skilled at facilitating personal growth and transformation, your progress will depend on your engagement and willingness to apply the insights and guidance you receive."
      }
    ],
    "Contact Us": [
      {
        id: "how-to-contact",
        question: "How can I get in touch with The Wisdom Network?",
        answer: <>If you have any questions or need assistance, please contact us through our contact page or email us at <a href="mailto:help@thewisdomnetwork.net">help@thewisdomnetwork.net</a>. We are here to help you every step of the way.</>
      }
    ]
  };

  return (
    <div className="faq-page">
      <div className="faq-paper">
        <section className="faq-intro">
          <div className="faq-content faq-intro__content">
            <h1 className="faq-title">
              Frequently Asked Questions (FAQ)
            </h1>
            <p className="faq-intro__description">
              Here, you'll find answers to the most commonly asked questions about our services, practitioners, and booking process.
            </p>

            <a
              className="faq-cta faq-cta--primary"
              href="https://thewisdomnetwork.as.me/"
              target="_blank"
              rel="noopener noreferrer"
            >
              SCHEDULE YOUR SESSION TODAY
            </a>
          </div>
        </section>

        <section className="faq-sections" aria-label="Frequently asked questions">
          <div className="faq-content">
            {Object.entries(faqData).map(([sectionTitle, questions]) => (
              <section className="faq-category" key={sectionTitle}>
                <h2 className="faq-category__title">
                  {sectionTitle}
                </h2>

                <ul className="faq-list">
                  {questions.map((faq) => {
                    const triggerId = `faq-question-${faq.id}`;
                    const panelId = `faq-answer-${faq.id}`;
                    const isOpen = Boolean(openQuestions[faq.id]);

                    return (
                      <li className="faq-item" key={faq.id}>
                        <button
                          id={triggerId}
                          type="button"
                          onClick={() => toggleQuestion(faq.id)}
                          className="faq-item__trigger"
                          aria-expanded={isOpen}
                          aria-controls={panelId}
                        >
                          <span className="faq-item__question">{faq.question}</span>
                          <ChevronDown
                            className={`faq-item__icon${isOpen ? ' is-open' : ''}`}
                            aria-hidden="true"
                          />
                        </button>

                        {isOpen && (
                          <div
                            className="faq-item__answer"
                            id={panelId}
                            role="region"
                            aria-labelledby={triggerId}
                          >
                            <p>{faq.answer}</p>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        </section>

        <section className="faq-closing">
          <div className="faq-content">
            <p className="faq-closing__message">
              We are here to help you every step of the way. Thank you for choosing The Wisdom Network!
            </p>
            <p className="faq-closing__tagline">
              The Wisdom Network: Verified Wisdom, Powerful Transformations
            </p>

            <div className="faq-closing__actions">
              <a
                className="faq-cta faq-cta--primary"
                href="https://thewisdomnetwork.as.me/"
                target="_blank"
                rel="noopener noreferrer"
              >
                SCHEDULE YOUR SESSION TODAY
              </a>
              <Link className="faq-cta faq-cta--secondary" to="/contact">
                CONTACT US
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default FAQPage;